import React, { useEffect, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { SiteMapSurfaceType, SurfaceType } from "../DataAndMap";
import { polygonArea } from "../utils/area";
import { polygonPerimeter } from "../utils/perimeter";
import { useMapEditing } from "../utils/routing";
import { useData } from "../contexts/DataContext";
import Select from "react-select";

const formatNumber = (
  num: number | string,
  options?: { decimals?: number },
) => {
  if (typeof num === "string") {
    num = parseFloat(num);
  }

  if (options && options.decimals !== undefined) {
    return num.toLocaleString(undefined, {
      minimumFractionDigits: options.decimals,
      maximumFractionDigits: options.decimals,
    });
  }

  return num.toLocaleString();
};

const SiteMapSurfaceRoute = ({
  surface,
  shouldCutHole,
  toggleHoleCutting,
}: {
  surface: SiteMapSurfaceType | undefined;
  shouldCutHole: boolean;
  toggleHoleCutting: () => void;
}) => {
  const { surfaceTypes } = useData();
  const geometry = useMapsLibrary("geometry");
  const { editing } = useMapEditing();
  const [area, setArea] = useState(0);
  const [perimeter, setPerimeter] = useState(0);

  // TODO: begin reading this value from company settings long-term
  const inFeet = true;

  useEffect(() => {
    if (geometry?.spherical && surface?.coordinates) {
      const calculatedArea = polygonArea(
        geometry.spherical.computeArea,
        surface.coordinates,
        inFeet,
      );
      setArea(calculatedArea);
    } else {
      setArea(0);
    }
  }, [geometry?.spherical, surface?.coordinates, inFeet]);

  useEffect(() => {
    if (geometry?.spherical && surface?.coordinates) {
      const calculatedPerimeter = polygonPerimeter(
        geometry.spherical.computeLength,
        surface.coordinates,
        inFeet,
      );
      setPerimeter(calculatedPerimeter);
    } else {
      setPerimeter(0);
    }
  }, [geometry?.spherical, surface?.coordinates, inFeet]);

  return (
    <>
      {!surface ? (
        <NoSurfaceFound />
      ) : editing ? (
        <EditSurfaceDetails
          surfaceTypes={surfaceTypes}
          surface={surface}
          area={area}
          perimeter={perimeter}
          inFeet={inFeet}
          shouldCutHole={shouldCutHole}
          toggleHoleCutting={toggleHoleCutting}
        />
      ) : (
        <ViewSurfaceDetails
          surfaceTypes={surfaceTypes}
          surface={surface}
          area={area}
          perimeter={perimeter}
          inFeet={inFeet}
        />
      )}
    </>
  );
};

export default SiteMapSurfaceRoute;

const SurfaceWrapper = ({
  children,
  title,
  footer,
}: {
  children: React.ReactNode;
  title: string;
  footer?: React.ReactNode;
}) => {
  return (
    <MapRouteSidebar title={title} footer={footer}>
      {children}
    </MapRouteSidebar>
  );
};

const NoSurfaceFound = () => {
  return (
    <SurfaceWrapper title="No Surface">
      <div style={{ color: "red" }}>No surface selected</div>
    </SurfaceWrapper>
  );
};

interface ViewSurfaceDetailsProps {
  surfaceTypes: SurfaceType[];
  surface: SiteMapSurfaceType;
  area: number;
  perimeter: number;
  inFeet: boolean;
}

const ViewSurfaceDetails = ({
  surfaceTypes,
  surface,
  area,
  perimeter,
  inFeet,
}: ViewSurfaceDetailsProps) => {
  const surfaceTypeLabel =
    surfaceTypes.find((type) => type.value === surface.properties.surfaceType)
      ?.label || surface.properties.surfaceType;

  return (
    <SurfaceWrapper title="View Surface">
      <div>
        <p>Type: {surfaceTypeLabel}</p>
        <p>
          Area: {formatNumber(area)} {inFeet ? "sq. ft." : "sq. m."}
        </p>
        <p>
          Perimeter: {formatNumber(perimeter)} {inFeet ? "ft." : "m."}
        </p>
      </div>
    </SurfaceWrapper>
  );
};

interface EditSurfaceDetailsProps {
  surfaceTypes: SurfaceType[];
  surface: SiteMapSurfaceType;
  area: number;
  perimeter: number;
  inFeet: boolean;
  shouldCutHole: boolean;
  toggleHoleCutting: () => void;
}

import { SingleValue } from "react-select";
import MapRouteSidebar, { MapRouteDelete } from "../components/MapRouteSidebar";
import colors from "tailwindcss/colors";
import { SurfaceIcon } from "../components/Icons";

const EditSurfaceDetails = ({
  surfaceTypes,
  surface,
  area,
  perimeter,
  inFeet,
  shouldCutHole,
  toggleHoleCutting,
}: EditSurfaceDetailsProps) => {
  const { deleteSurfaceFeature, updateSurfaceFeature } = useData();

  const handleSurfaceTypeChange = (newValue: SingleValue<SurfaceType>) => {
    if (!newValue) return;

    updateSurfaceFeature({
      ...surface,
      properties: {
        ...surface.properties,
        surfaceType: newValue.value,
      },
    });
  };

  return (
    <>
      <SurfaceWrapper title="Edit Surface">
        <div>
          <div className="mb-5">
            <label
              htmlFor="surfaceType"
              style={{ display: "block", marginBottom: "5px" }}
            >
              Surface Type
            </label>
            <Select
              placeholder="Select Surface Type"
              options={surfaceTypes}
              value={surfaceTypes.find(
                (option) => option.value === surface.properties.surfaceType,
              )}
              onChange={handleSurfaceTypeChange}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: "#829399",
                  minHeight: "20px",
                  lineHeight: "18px",
                  fontSize: "14px",
                }),
                dropdownIndicator: (baseStyles) => ({
                  ...baseStyles,
                  padding: "4px",
                }),
                menu: (css) => ({
                  ...css,
                  fontSize: "14px",
                }),
              }}
            />
          </div>
          <p>
            Area: {formatNumber(area)} {inFeet ? "sq. ft." : "sq. m."}
          </p>
          <p>
            Perimeter: {formatNumber(perimeter)} {inFeet ? "ft." : "m."}
          </p>
        </div>
        <MapRouteDelete onDelete={() => deleteSurfaceFeature(surface)} />
      </SurfaceWrapper>
    </>
  );
};
